import { Alert, Col, Divider, InputNumber, Row, Select, Typography } from "antd";
import { useContext, useEffect, useState } from "react";
import { Controller, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import AppContext from "../../../context/app/context";
import { ICategory } from "../../../models/category/response";
import EndPoints from "../../../services/end-points";
import FieldBuilder from "../../form-components/field-builder";
import FormItem from "../../general/form-item";

export const DiscountDetailsSection = ({ control, setValue }) => {
    const { t } = useTranslation();
    const { direction } = useContext(AppContext);
    const typeApp = useWatch({ control, name: "typeApp" });
    const typeValue = useWatch({ control, name: "typeValue" });
    const isSubscriptionCoupon = useWatch({ control, name: "isSubscriptionCoupon" });
    const [categories, setCategories] = useState<ICategory[]>([]);
    const [categoriesLoading, setCategoriesLoading] = useState(false);
    const showCategoryTarget = Number(typeValue) === 2 && [2, 3].includes(Number(typeApp));
    const showDeliveryDiscountMaxOfUse = !!isSubscriptionCoupon && Number(typeValue) === 2 && [1, 3].includes(Number(typeApp));
    const isPercentValue = Number(typeValue) === 2;
    const discountValueTypeLabel = isPercentValue ? t("percent") : t("fixed_amount");
    const discountValueSuffix = isPercentValue ? "%" : t("currency");
    const categoryDiscounts = useWatch({ control, name: "categoryDiscounts" }) ?? [];
    const selectedCategoryIds = Array.isArray(categoryDiscounts)
        ? categoryDiscounts
            .map((categoryDiscount) => Number(categoryDiscount?.categoryId))
            .filter((categoryId) => Number.isFinite(categoryId) && categoryId > 0)
        : [];
    const hasCategoryDiscounts = selectedCategoryIds.length > 0;
    const showMainProductDiscount = Number(typeApp) !== 1 && !hasCategoryDiscounts;

    const byInvitation = useWatch({
        control,
        name: "isInvitation",
    });
    const type = useWatch({
        control,
        name: "type",
    });
    const userType = useWatch({
        control,
        name: "userType",
    });

    useEffect(() => {
        if (!showCategoryTarget) {
            setValue("categoryIds", undefined);
            setValue("categoryDiscounts", undefined);
            return;
        }

        let isActive = true;
        const getCategories = async () => {
            setCategoriesLoading(true);
            try {
                const { data } = await EndPoints.category.getAllCategories({
                    light: true,
                    perPage: -1,
                });
                if (isActive) {
                    setCategories(data?.data ?? []);
                }
            } catch (error) {
                if (isActive) {
                    setCategories([]);
                }
            } finally {
                if (isActive) {
                    setCategoriesLoading(false);
                }
            }
        };

        getCategories();

        return () => {
            isActive = false;
        };
    }, [showCategoryTarget, direction, setValue]);

    useEffect(() => {
        if (hasCategoryDiscounts) {
            setValue("discountValueProduct", undefined, { shouldDirty: true, shouldValidate: true });
        }
    }, [hasCategoryDiscounts, setValue]);

    useEffect(() => {
        if (!showDeliveryDiscountMaxOfUse) {
            setValue("deliveryDiscountMaxOfUse", undefined);
        }
    }, [showDeliveryDiscountMaxOfUse, setValue]);

    const getCategoryLabel = (categoryId: number) =>
        categories.find((category) => Number(category?.id) === Number(categoryId))?.name ??
        `#${categoryId}`;

    const handleCategoryDiscountCategoriesChange = (categoryIds: number[]) => {
        const currentDiscounts = Array.isArray(categoryDiscounts) ? categoryDiscounts : [];
        const currentDiscountByCategoryId = new Map(
            currentDiscounts.map((categoryDiscount) => [
                Number(categoryDiscount?.categoryId),
                categoryDiscount,
            ])
        );

        setValue(
            "categoryDiscounts",
            categoryIds.map((categoryId) => {
                const existingDiscount = currentDiscountByCategoryId.get(Number(categoryId));

                return {
                    categoryId: Number(categoryId),
                    discountValueProduct: existingDiscount?.discountValueProduct,
                };
            }),
            { shouldDirty: true, shouldValidate: true }
        );
    };

    return (
        <>
            <Divider orientation="center">{t("discount_details")}</Divider>
            <Row gutter={[16, 8]}>
                {(type != 1 || byInvitation || userType == 3) && <Col xs={24} sm={12} md={8} lg={8} xl={8} xxl={8}>
                    <FieldBuilder
                        name={"codeValidity"}
                        label={t("code_validity")}
                        input={{ type: "number" }}
                        rules={{ required: type != 1 || byInvitation || userType == 3 }}
                    />
                </Col>}
                {!byInvitation && type != 1 && <Col xs={24} sm={12} md={8} lg={8} xl={8} xxl={8}>
                    <FieldBuilder
                        name={"numberPoints"}
                        label={t("number_points")}
                        input={{ type: "number" }}
                        rules={{ required: type == 2 }}
                        disabled={type == 1 || byInvitation}
                    />
                </Col>}
                {!byInvitation && type == 1 && userType != 3 && <Col xs={24} sm={12} md={8} lg={8} xl={8} xxl={8}>
                    <FieldBuilder
                        name="validityDate"
                        rules={{ required: false }}
                        label={`${t("validity_date")}`}
                        input={{ type: "date-picker", format: "YYYY-MM-DD" }}
                    />
                </Col>}

                {showMainProductDiscount && <Col xs={24} sm={12} md={8} lg={8} xl={8} xxl={8}>
                    <FieldBuilder
                        name={"discountValueProduct"}
                        label={`${t("discountValueProduct")} (${discountValueTypeLabel})`}
                        rules={{ required: true }}
                        input={{ type: "number" }}
                        suffix={discountValueSuffix}
                    />
                </Col>}
                {showCategoryTarget && (
                    <>
                        <Col xs={24} sm={24} md={16} lg={16} xl={16} xxl={16}>
                            <FormItem label={t("coupon_product_categories")}>
                                <Select
                                    mode="multiple"
                                    allowClear
                                    showSearch
                                    loading={categoriesLoading}
                                    value={selectedCategoryIds}
                                    optionFilterProp="label"
                                    placeholder={t("coupon_product_categories")}
                                    style={{ width: "100%" }}
                                    options={categories?.map((category) => ({
                                        label: category?.name,
                                        value: category?.id,
                                    }))}
                                    onChange={handleCategoryDiscountCategoriesChange}
                                />
                            </FormItem>
                        </Col>
                        {categoryDiscounts?.map((categoryDiscount, index) => (
                            <Col
                                xs={24}
                                sm={12}
                                md={8}
                                lg={8}
                                xl={8}
                                xxl={8}
                                key={categoryDiscount?.categoryId ?? index}
                            >
                                <FormItem
                                    label={`${getCategoryLabel(categoryDiscount?.categoryId)} - ${t("discountValueProduct")}`}
                                    required
                                >
                                    <Controller
                                        name={`categoryDiscounts.${index}.discountValueProduct`}
                                        control={control}
                                        rules={{
                                            required: true,
                                            min: 1,
                                            max: 100,
                                        }}
                                        render={({ field, fieldState }) => (
                                            <>
                                                <InputNumber
                                                    {...field}
                                                    min={1}
                                                    max={100}
                                                    addonAfter="%"
                                                    status={fieldState.error ? "error" : undefined}
                                                    style={{ width: "100%" }}
                                                    onChange={(value) => field.onChange(value)}
                                                />
                                                {fieldState.error && (
                                                    <Typography.Text type="danger">
                                                        {t("category_discount_value_range")}
                                                    </Typography.Text>
                                                )}
                                            </>
                                        )}
                                    />
                                </FormItem>
                            </Col>
                        ))}
                        <Col span={24}>
                            <Alert
                                showIcon
                                type="info"
                                message={t(
                                    Number(typeApp) === 3
                                        ? "coupon_product_drive_categories_note"
                                        : "coupon_product_categories_note"
                                )}
                            />
                        </Col>
                    </>
                )}
                {typeApp != 2 && <Col xs={24} sm={12} md={8} lg={8} xl={8} xxl={8}>
                    <FieldBuilder
                        name={"discountValueDelivery"}
                        label={`${t("discountValueDelivery")} (${discountValueTypeLabel})`}
                        input={{ type: "number" }}
                        suffix={discountValueSuffix}
                        rules={{ required: typeApp != 2 ? true : false }}
                        disabled={typeApp == 2 ? true : false}
                    />
                </Col>}
                {showDeliveryDiscountMaxOfUse && (
                    <Col xs={24} sm={12} md={8} lg={8} xl={8} xxl={8}>
                        <FieldBuilder
                            name="deliveryDiscountMaxOfUse"
                            label={t("delivery_discount_max_of_use")}
                            input={{ type: "number" }}
                            rules={{
                                min: 1,
                                validate: (value) =>
                                    value === undefined ||
                                    value === null ||
                                    value === "" ||
                                    Number.isInteger(Number(value)) ||
                                    t("integer_value_required"),
                            }}
                        />
                    </Col>
                )}
            </Row>
            <Divider orientation="center">{t("usage_limits")}</Divider>
            <Row gutter={[16, 8]}>
                <Col xs={24} sm={12} md={8} lg={8} xl={8} xxl={8}>
                    <FieldBuilder
                        name="minimumLimit"
                        label={t("minimum_limit_coupon")}
                        input={{ type: "number" }}
                        rules={{ required: true }}
                    />
                </Col>
                <Col xs={24} sm={12} md={8} lg={8} xl={8} xxl={8}>
                    <FieldBuilder
                        name="upperLimit"
                        label={t("maximum_limit_coupon")}
                        input={{ type: "number" }}
                        rules={{ required: true }}
                    />
                </Col>
                <Col xs={24} sm={12} md={8} lg={8} xl={8} xxl={8}>
                    <FieldBuilder
                        name={"maxOfUse"}
                        label={t("frequency_of_use")}
                        input={{ type: "number" }}
                        rules={{ required: true }}
                    />
                </Col>
                <Col xs={24} sm={12} md={8} lg={8} xl={8} xxl={8}>
                    <FieldBuilder
                        name="numberOfUseAllowed"
                        label={t("numberOfUsersAllowed")}
                        input={{ type: "number" }}
                        rules={{ required: true }}
                    />
                </Col>

            </Row>
        </>
    );
};
